import React, { useCallback, useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';

import { BdsInput, BdsModal } from 'blip-ds/dist/blip-ds-react';
import { ModalContent } from './styles';
import { KnowledgeBaseApi } from '../../../api/KnowledgeBaseApi';
import { showToast } from '../../../api/CommonApi';
import { useContentLocalizer } from '../../../hooks/useContentLocalizer';
import { useModalManagement } from '../../../hooks/useModalManagement';
import { useKnowledgeBaseTracking } from '../../../hooks/useKnowledgeBaseTracking';
import { ModalHeader } from '../../../components/ModalHeader';
import { ModalFooter } from '../../../components/ModalFooter';
import { localization } from './localization';
import {
  KNOWLEDGE_CATALOG_CREATE,
  KNOWLEDGE_CATALOG_SAVE,
  KNOWLEDGE_CATALOG_CANCEL,
} from '../../../shared/SegmentEvents';

interface CreateCatalogModalProps {
  isOpen: boolean;
  tenantId: string;
  onClose: (wasCancelled: boolean) => void;
}

const CreateCatalogModal: React.FC<CreateCatalogModalProps> = ({ isOpen, tenantId, onClose }) => {
  const [catalogName, setCatalogName] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const translate = useContentLocalizer(localization);
  const history = useHistory();
  const { trackKnowledgeBaseEvent } = useKnowledgeBaseTracking();

  const resetForm = useCallback(() => {
    setCatalogName('');
    setIsLoading(false);
  }, []);

  const handleTrackingCancel = useCallback(() => {
    if (isLoading) return;
    trackKnowledgeBaseEvent(KNOWLEDGE_CATALOG_CANCEL);
  }, [isLoading, trackKnowledgeBaseEvent]);

  const { modalRef, handleCancel } = useModalManagement({
    isOpen,
    onClose,
    resetForm,
    onCancel: handleTrackingCancel,
    shouldAllowCancel: !isLoading,
  });

  const handleCatalogNameChange = useCallback((event: CustomEvent) => {
    setCatalogName(event.detail.value.trim());
  }, []);

  const handleCreateCatalog = useCallback(async () => {
    if (!isFormValid || isLoading) {
      return;
    }

    try {
      setIsLoading(true);
      const createdCatalog = await KnowledgeBaseApi.createCatalogAsync(tenantId, catalogName);

      trackKnowledgeBaseEvent(KNOWLEDGE_CATALOG_SAVE, {
        knowledgeName: catalogName,
      });

      showToast({
        title: translate.successToast.title,
        message: translate.successToast.message,
        type: 'success',
      });

      resetForm();
      onClose(false);

      history.push(`/knowledge-base/${createdCatalog.id}/documents`);
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Error creating catalog:', error);
      showToast({
        title: translate.errorToast.title,
        message: translate.errorToast.message,
        type: 'danger',
      });
    } finally {
      setIsLoading(false);
    }
  }, [catalogName, tenantId, onClose, resetForm, history, trackKnowledgeBaseEvent, translate, isLoading]);

  // Track modal open event
  useEffect(() => {
    if (isOpen) {
      trackKnowledgeBaseEvent(KNOWLEDGE_CATALOG_CREATE);
      resetForm();
    }
  }, [isOpen, resetForm, trackKnowledgeBaseEvent]);

  const isFormValid = catalogName.length > 0;

  return (
    <BdsModal
      ref={modalRef}
      open={isOpen}
      size="dynamic"
      closeButton={!isLoading}
      outzoneClose={!isLoading}
      enterClose={false}
    >
      <ModalContent direction="column" gap="3">
        <ModalHeader title={translate.title} subtitle={translate.subtitle} />

        <BdsInput
          placeholder={translate.input.placeholder}
          onBdsChange={handleCatalogNameChange}
          value={catalogName}
          disabled={isLoading}
          required
          danger={catalogName.length === 0}
          errorMessage={translate.input.requiredErrorMessage}
          requiredErrorMessage={translate.input.requiredErrorMessage}
        />

        <ModalFooter
          cancelText={translate.cancelButton}
          saveText={translate.saveButton}
          isLoading={isLoading}
          isFormValid={isFormValid}
          onCancel={handleCancel}
          onSave={handleCreateCatalog}
        />
      </ModalContent>
    </BdsModal>
  );
};

export default CreateCatalogModal;
